"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.alphanumericWithDashesUnderscoreAndPeriodRegex = exports.camelCaseToTitleCase = exports.toTitleCase = exports.keyValueStringToObject = exports.keyValuePairsToArray = exports.splitToList = exports.replaceLoneSurrogates = exports.truncateString = exports.pascalCase = exports.isValidEmail = exports.stripHtmlFromString = exports.unCapitalizeFirst = exports.isNumeric = exports.digitsToText = exports.collapseWhitespace = exports.capitalizeFirstOnly = exports.capitalizeFirst = exports.hyphenCase = exports.camelCase = void 0;
/**
 * Converts to camelCase
 *
 * @param value the value to convert
 */
function camelCase(value) {
    return value
        .trim()
        .toLowerCase()
        .replaceAll(/[^\dA-Za-z]+(.)/g, (m, chr) => chr.toUpperCase());
}
exports.camelCase = camelCase;
/**
 * Converts to hyphen-case
 *
 * @param value the value to convert
 */
function hyphenCase(value) {
    return value.replaceAll(/(\s[A-Z]|[A-Z]|\s)/g, (match1, match2, offset, match) => {
        var _a;
        return ((offset === 0 || ((_a = match.at(offset - 1)) === null || _a === void 0 ? void 0 : _a.match(/[_-]/)) ? '' : '-') +
            match1.toLowerCase().replace(/\s/, ''));
    });
}
exports.hyphenCase = hyphenCase;
/**
 * Converts first character to uppercase.
 *
 * @param value the value to convert
 */
function capitalizeFirst(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
exports.capitalizeFirst = capitalizeFirst;
/**
 * Replaces digits with text. Only handles English, so should not be used in customer-facing components
 * @param value A single character, must be an Arabic numeral.
 * @returns English word for that number
 */
function digitToText(chr) {
    var _a;
    const map = {
        '0': 'zero',
        '1': 'one',
        '2': 'two',
        '3': 'three',
        '4': 'four',
        '5': 'five',
        '6': 'six',
        '7': 'seven',
        '8': 'eight',
        '9': 'nine'
    };
    return (_a = map[chr]) !== null && _a !== void 0 ? _a : 'Not a digit';
}
/**
 * Replaces digits with text. Only handles English, so should not be used in customer-facing components
 * @param value String with digits e.g. 'Section-1'
 * @param transform Optional function to run on each digit to transform its value
 * @returns String wtih digits replaced with words e.g. 'Section-One'
 */
function digitsToText(value, transform) {
    return value.replaceAll(/(\d)/g, (m, chr) => {
        const text = digitToText(chr);
        return transform ? transform(text) : text;
    });
}
exports.digitsToText = digitsToText;
/**
 * Converts to PascalCase
 * @param value the value to convert
 */
function pascalCase(value) {
    return capitalizeFirst(camelCase(value));
}
exports.pascalCase = pascalCase;
/**
 * Capitalize first character and sets the rest to lower case.
 *
 * @param value the value to convert
 */
function capitalizeFirstOnly(value) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
exports.capitalizeFirstOnly = capitalizeFirstOnly;
/**
 * Collapses all whitespace down to a single space.
 * So "a   sentence   like        this" will become "a sentence like this".
 * @param string the string to collapse whitespace in.
 */
function collapseWhitespace(string) {
    return string.replaceAll(/\s+/g, ' ');
}
exports.collapseWhitespace = collapseWhitespace;
/**
 * Converts first character to lowercase.
 *
 * @param value the value to convert
 */
function unCapitalizeFirst(value) {
    return value.charAt(0).toLowerCase() + value.slice(1);
}
exports.unCapitalizeFirst = unCapitalizeFirst;
/**
 * Strips the HTML from a string.
 * @param value
 */
function stripHtmlFromString(value) {
    return value.replaceAll(/(<([^>]+)>)/gi, '');
}
exports.stripHtmlFromString = stripHtmlFromString;
// This regex was copied from lithium.fusion.validation.AbstractEmailSpecialCheck#VALID_EMAIL_BLOCK_REGEX
// and simplified via our eslint rule (unicorn/better-regex)
const validEmailRegex = new RegExp(/^[\w!#$%&'*-/=?^`{|}~]+\S@[\w!#$%&'*-/=?^`{|}~]+\.[\w!#$%&'*-/=?^`{|}~]+$/);
/**
 * Check is a given string can be _directly_ represented as a number
 * See tests or find usages for deeper understanding
 */
function isNumeric(value) {
    /*
     * Number.isNaN does not do the same thing as global isNaN and will cause tests to fail
     * Also, global isNaN accepts types other than number, so this is also bypassed with any
     */
    // eslint-disable-next-line unicorn/prefer-number-properties, @typescript-eslint/no-explicit-any
    return !!value && !isNaN(value);
}
exports.isNumeric = isNumeric;
/**
 * Check if given string is a 'valid' email
 *
 * @param email
 */
function isValidEmail(email) {
    return validEmailRegex.test(email);
}
exports.isValidEmail = isValidEmail;
/**
 * Truncate a string to given character length if it exceeds that length.
 * Truncates to the last complete word.
 * Append ellipses to the end of the truncated string if required.
 *
 * @param string the value to truncate
 * @param maxLength the number of characters to keep
 * @param addEllipses true/false for need to append ellipse
 */
function truncateString(string, maxLength, addEllipses = true) {
    if (string.length <= maxLength) {
        return string;
    }
    const separator = ' ';
    // Total length of truncated string with ellipses should not exceed maxLength
    const length = addEllipses ? maxLength - 3 : maxLength;
    // Trim the string to the maximum length plus next character.
    let trimmedString = string.slice(0, length + 1);
    // Re-trim if we are in the middle of a word
    trimmedString = trimmedString.split(separator).slice(0, -1).join(separator);
    return addEllipses ? `${trimmedString}...` : trimmedString;
}
exports.truncateString = truncateString;
/**
 * Replace unicode lone surrogates with the specified replace value. This is useful for removing
 * any lone surrogates that may have resulted from a truncated string.
 *
 * https://jira.dev.lithium.com/browse/LIA-88879
 *
 * https://2ality.com/2017/07/regexp-unicode-property-escapes.html#examples
 * https://stackoverflow.com/a/74497592
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/unicode#unicode-aware_mode
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape
 * https://mathiasbynens.be/notes/es6-unicode-regex
 * https://262.ecma-international.org/6.0/#sec-get-regexp.prototype.unicode
 * https://gist.github.com/mathiasbynens/bbe7f870208abcfec860
 *
 * @param value the value
 * @param replaceValue the replace value
 */
function replaceLoneSurrogates(value, replaceValue = '') {
    return value.replaceAll(/\p{Surrogate}/gu, replaceValue);
}
exports.replaceLoneSurrogates = replaceLoneSurrogates;
/**
 * Split a string into a list of strings based on whitespace and trim the results.
 * @param value
 */
function splitToList(value) {
    var _a, _b;
    return (_b = (_a = value === null || value === void 0 ? void 0 : value.split(/[\s,]+/)) === null || _a === void 0 ? void 0 : _a.map(item => item.trim())) === null || _b === void 0 ? void 0 : _b.filter(item => (item === null || item === void 0 ? void 0 : item.length) > 0);
}
exports.splitToList = splitToList;
/**
 * Parses `=` separated key/value pairs into an array of objects.
 *
 * @param keyValuePairs the key value pairs separated by an = sign
 */
function keyValuePairsToArray(keyValuePairs) {
    var _a;
    if (!keyValuePairs) {
        return [];
    }
    return (_a = keyValuePairs === null || keyValuePairs === void 0 ? void 0 : keyValuePairs.map(keyValuePair => { var _a; return (_a = keyValuePair.split('=')) === null || _a === void 0 ? void 0 : _a.filter(token => (token === null || token === void 0 ? void 0 : token.trim()) !== ''); })) === null || _a === void 0 ? void 0 : _a.map(tokenArray => ({
        [tokenArray[0]]: tokenArray[1]
    }));
}
exports.keyValuePairsToArray = keyValuePairsToArray;
/**
 * Parses `=` separated key/value pairs into an object with properties corresponding to the keys and values.
 *
 * @param keyValueString a string of key value pairs separated by an = sign
 */
function keyValueStringToObject(keyValueString) {
    var _a;
    if (!keyValueString) {
        return {};
    }
    const keyValuePairs = splitToList(keyValueString);
    return (_a = keyValuePairs === null || keyValuePairs === void 0 ? void 0 : keyValuePairs.map(keyValuePair => { var _a; return (_a = keyValuePair.split('=')) === null || _a === void 0 ? void 0 : _a.filter(token => (token === null || token === void 0 ? void 0 : token.trim()) !== ''); })) === null || _a === void 0 ? void 0 : _a.reduce((result, item) => {
        const [key, value] = item;
        result[key] = value;
        return result;
    }, {});
}
exports.keyValueStringToObject = keyValueStringToObject;
function toTitleCase(inputString) {
    return inputString.toLowerCase().replaceAll(/\b\w/g, char => char.toUpperCase());
}
exports.toTitleCase = toTitleCase;
/**
 * Converts camelCase String to Title Case Eg: helloWorld -> Hello World
 *
 * @param inputString - input string to convert
 */
function camelCaseToTitleCase(inputString) {
    return inputString
        .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
        .replaceAll(/\b\w/g, char => char.toUpperCase());
}
exports.camelCaseToTitleCase = camelCaseToTitleCase;
/**
 * A regex to allow alphanumeric, '-', '_', and '.' characters only.
 */
const alphanumericWithDashesUnderscoreAndPeriodRegex = new RegExp(/^[\w.\-]+$/);
exports.alphanumericWithDashesUnderscoreAndPeriodRegex = alphanumericWithDashesUnderscoreAndPeriodRegex;
//# sourceMappingURL=StringHelper.js.map